import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { alertService, AlertType } from '../_services/alert.service';

const propTypes = {
    id: PropTypes.string,
    fade: PropTypes.bool
};

const defaultProps = {
    id: 'default-alert',
    fade: true
};

function Alert({ id, fade }) {
    const history = useHistory();
    const [alerts, setAlerts] = useState([]);

    useEffect(() => {
        // subscribe to new alert notifications
        const subscription = alertService.onAlert(id)
            .subscribe(alert => {
                // clear alerts when an empty alert is received
                if (!alert.message) {
                    setAlerts(alerts => {
                        // filter out alerts without 'keepAfterRouteChange' flag
                        const filteredAlerts = alerts.filter(x => x.keepAfterRouteChange);

                        // remove 'keepAfterRouteChange' flag on the rest
                        filteredAlerts.forEach(x => delete x.keepAfterRouteChange);
                        return filteredAlerts;
                    });
                } else {
                    // add alert to array
                    setAlerts(alerts => ([...alerts, alert]));

                    // auto close alert if required
                    if (alert.autoClose) {
                        setTimeout(() => removeAlert(alert), 3000);
                    }
                }
            });

        // clear alerts on location change
        const historyUnlisten = history.listen(() => {
            alertService.clear(id);
        });

        // clean up function that runs when the component unmounts
        return () => {
            // unsubscribe & unlisten to avoid memory leaks
            subscription.unsubscribe();
            historyUnlisten();
        };
    }, []);

    function removeAlert(alert) {
        if (fade) {
            // fade out alert
            const alertWithFade = { ...alert, fade: true };
            setAlerts(alerts => alerts.map(x => x === alert ? alertWithFade : x));

            // remove alert after faded out
            setTimeout(() => {
                setAlerts(alerts => alerts.filter(x => x !== alertWithFade));
            }, 250);
        } else {
            // remove alert
            setAlerts(alerts => alerts.filter(x => x !== alert));
        }
    }

    function cssClasses(alert) {
        if (!alert) return;

        const classes = ['alert', 'alert-dismissable'];

        const alertTypeClass = {
            [AlertType.Success]: 'alert alert-success',
            [AlertType.Error]: 'alert alert-danger',
            [AlertType.Info]: 'alert alert-info',
            [AlertType.Warning]: 'alert alert-warning'
        }

        classes.push(alertTypeClass[alert.type]);
        classes.push(alert.position);
        if (alert.fade) {
            classes.push('fade');
        }

        return classes.join(' ');
    }

    function cssIconClass(alert) {
        if (!alert) return;

        const alertIconClass = {
            [AlertType.Success]: 'icon-fi-sr-check',
            [AlertType.Error]: ' icon-fi-rr-cross-circle',
            [AlertType.Info]: 'icon-fi-rr-exclamation',
            [AlertType.Warning]: 'icon-fi-rr-interrogation'
        }

        return alertIconClass[alert.type];
    }

    if (!alerts.length) return null;

    return (
        alerts.map((alert, index) =>
            <div key={index} className={cssClasses(alert)}>
                <table>
                    <tbody>
                        <tr>
                            <td className="icon-container">
                                <span className={cssIconClass(alert)}></span>
                            </td>
                            <td className="message-container">
                                <a className="close" onClick={() => removeAlert(alert)}>&times;</a>
                                <div className="alert-header">{alert.header}</div>
                                <div className="alert-message">
                                    <div dangerouslySetInnerHTML={{ __html: alert.message }}>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    );
}

Alert.propTypes = propTypes;
Alert.defaultProps = defaultProps;
export default Alert;